import { useState } from 'react'
import { motion } from 'framer-motion'
import {
  ShieldAlert, Zap, TrendingUp, Users, Banknote,
  Clock, Eye, XCircle, UserX, Search,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { fadeInUp, staggerContainer, cardReveal } from '@/lib/motion'
import { Drawer } from 'vaul'
import { useToast } from '@/components/shared/Toast'

// ── Types ──────────────────────────────────────────────

type Severity = 'critical' | 'warning' | 'info'
type Status = 'pending' | 'investigating' | 'dismissed'
type AlertType = 'win_streak' | 'rapid_switch' | 'win_rate' | 'collusion' | 'large_withdrawal'
type ActionMode = null | 'suspend' | 'dismiss'

interface FraudAlert {
  id: string
  type: AlertType
  severity: Severity
  title: string
  desc: string
  user: string
  time: string
  status: Status
}

// ── Mock Data ──────────────────────────────────────────

const alerts: FraudAlert[] = [
  { id: 'F001', type: 'win_streak', severity: 'critical', title: '異常連勝', desc: '用戶連續贏得 12 手，勝率異常', user: 'Lucky88', time: '3 分鐘前', status: 'pending' },
  { id: 'F002', type: 'rapid_switch', severity: 'warning', title: '快速換桌', desc: '用戶在 10 分鐘內切換 5 個房間', user: '大衛王', time: '15 分鐘前', status: 'investigating' },
  { id: 'F003', type: 'win_rate', severity: 'warning', title: '勝率異常', desc: '最近 100 手勝率達 72%，高於平均值 2.5 倍', user: 'PokerKing', time: '1 小時前', status: 'pending' },
  { id: 'F004', type: 'collusion', severity: 'info', title: '疑似串通', desc: '兩用戶經常在同桌，且資金流向單一方向', user: '小明 & 阿花', time: '2 小時前', status: 'dismissed' },
  { id: 'F005', type: 'large_withdrawal', severity: 'info', title: '大額提款', desc: '單筆提款金額超過 $5,000', user: 'Lucky88', time: '3 小時前', status: 'dismissed' },
]

const userDetails: Record<string, { balance: string; hands: number; totalDeposit: string; totalWithdraw: string; rakeCollected: string }> = {
  Lucky88:       { balance: '$12,340', hands: 847, totalDeposit: '$8,000', totalWithdraw: '$2,500', rakeCollected: '$423' },
  '大衛王':       { balance: '$3,210',  hands: 312, totalDeposit: '$5,000', totalWithdraw: '$5,000', rakeCollected: '$156' },
  PokerKing:     { balance: '$8,750',  hands: 1203, totalDeposit: '$12,000', totalWithdraw: '$9,500', rakeCollected: '$721' },
  '小明 & 阿花':  { balance: '$6,100',  hands: 560, totalDeposit: '$4,200', totalWithdraw: '$1,800', rakeCollected: '$280' },
}

const evidenceMap: Record<string, string[]> = {
  F001: ['連續贏 12 手 (過去 45 分鐘)', '勝率 87% (最近 50 手)', '平均底池 $120，遠高於桌面均值 $45', 'IP 地址與另一玩家重疊'],
  F002: ['10 分鐘內切換 5 個房間', '每桌僅停留 1-2 手即離開', '離桌時皆為正盈利', '行為模式符合「探桌」策略'],
  F003: ['最近 100 手勝率 72%', '平台平均勝率 28%', '高額桌位勝率更高 (81%)', '疑似使用輔助軟體'],
  F004: ['兩用戶 30 天同桌次數: 47 次', '資金流向: 小明 → 阿花 $2,800', '下注模式高度相關 (r=0.91)', '兩帳號 IP 位址相同子網段'],
  F005: ['單筆提款 $5,200', '帳戶餘額驟降 92%', '過去 7 天充值總額 $1,500', '提款金額遠超充值記錄'],
}

// ── Severity / Status Configs ──────────────────────────

const severityConfig: Record<Severity, { color: string; bg: string; text: string; label: string; dot: string }> = {
  critical: { color: 'bg-red-500', bg: 'bg-red-500/10', text: 'text-red-400', label: '嚴重', dot: 'bg-red-500 animate-pulse' },
  warning:  { color: 'bg-amber-500', bg: 'bg-amber-500/10', text: 'text-amber-400', label: '警告', dot: 'bg-amber-500' },
  info:     { color: 'bg-blue-500', bg: 'bg-blue-500/10', text: 'text-blue-400', label: '資訊', dot: 'bg-blue-500' },
}

const statusConfig: Record<Status, { label: string; style: string }> = {
  pending:       { label: '未處理', style: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  investigating: { label: '調查中', style: 'bg-blue-500/10 text-blue-400 border-blue-500/20' },
  dismissed:     { label: '已忽略', style: 'bg-foreground/5 text-muted-foreground border-foreground/10' },
}

const typeIcon: Record<AlertType, typeof ShieldAlert> = {
  win_streak: TrendingUp,
  rapid_switch: Zap,
  win_rate: TrendingUp,
  collusion: Users,
  large_withdrawal: Banknote,
}

// ── Component ──────────────────────────────────────────

export function AdminFraudScreen() {
  const { toast } = useToast()
  const [alertList, setAlertList] = useState<FraudAlert[]>(alerts)
  const [selectedAlert, setSelectedAlert] = useState<FraudAlert | null>(null)
  const [actionMode, setActionMode] = useState<ActionMode>(null)
  const [suspendReason, setSuspendReason] = useState('')
  const [dismissReason, setDismissReason] = useState('')

  const counts = {
    critical: alertList.filter((a) => a.severity === 'critical').length,
    warning:  alertList.filter((a) => a.severity === 'warning').length,
    info:     alertList.filter((a) => a.severity === 'info').length,
  }

  const detail = selectedAlert ? userDetails[selectedAlert.user] : null
  const evidence = selectedAlert ? evidenceMap[selectedAlert.id] ?? [] : []

  const closeDrawer = () => {
    setSelectedAlert(null)
    setActionMode(null)
    setSuspendReason('')
    setDismissReason('')
  }

  const handleSuspendConfirm = () => {
    if (!selectedAlert || !suspendReason.trim()) return
    setAlertList((prev) => prev.map((a) => a.id === selectedAlert.id ? { ...a, status: 'dismissed' as Status } : a))
    toast({ type: 'success', title: '帳號已停用', description: `用戶 ${selectedAlert.user} 已被停用` })
    closeDrawer()
  }

  const handleMarkInvestigating = () => {
    if (!selectedAlert) return
    setAlertList((prev) => prev.map((a) => a.id === selectedAlert.id ? { ...a, status: 'investigating' as Status } : a))
    toast({ type: 'info', title: '已標記調查', description: `警報 ${selectedAlert.id} 已標記為調查中` })
    closeDrawer()
  }

  const handleDismissConfirm = () => {
    if (!selectedAlert || !dismissReason.trim()) return
    setAlertList((prev) => prev.map((a) => a.id === selectedAlert.id ? { ...a, status: 'dismissed' as Status } : a))
    toast({ type: 'success', title: '警報已忽略', description: `警報 ${selectedAlert.id} 已忽略` })
    closeDrawer()
  }

  return (
    <motion.div
      variants={staggerContainer}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      <div className="p-4 pb-8 flex flex-col gap-5">
        {/* ── Alert Summary ── */}
        <motion.div variants={fadeInUp} className="grid grid-cols-3 gap-2">
          {(['critical', 'warning', 'info'] as Severity[]).map((sev) => {
            const cfg = severityConfig[sev]
            return (
              <motion.div key={sev} variants={cardReveal} className={cn('p-3 rounded-2xl glass-card border text-center', cfg.bg, `border-${sev === 'critical' ? 'red' : sev === 'warning' ? 'amber' : 'blue'}-500/20`)}>
                <div className="flex items-center justify-center gap-1.5 mb-1">
                  <span className={cn('w-2 h-2 rounded-full', cfg.dot)} />
                  <span className={cn('text-[10px] font-bold', cfg.text)}>{cfg.label}</span>
                </div>
                <p className={cn('text-2xl font-bold font-mono', cfg.text)}>{counts[sev]}</p>
              </motion.div>
            )
          })}
        </motion.div>

        {/* ── Alert List ── */}
        <motion.section variants={fadeInUp} className="space-y-2">
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">警報列表</h3>
          {alertList.map((alert) => {
            const sev = severityConfig[alert.severity]
            const sts = statusConfig[alert.status]
            const Icon = typeIcon[alert.type]

            return (
              <motion.div
                key={alert.id}
                variants={cardReveal}
                onClick={() => setSelectedAlert(alert)}
                className="flex rounded-xl glass-card overflow-hidden cursor-pointer active:scale-[0.98] transition-transform"
              >
                {/* Severity color bar */}
                <div className={cn('w-1 flex-shrink-0', sev.color)} />

                <div className="flex-1 p-3 flex gap-3">
                  <div className={cn('w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0', sev.bg)}>
                    <Icon className={cn('w-4 h-4', sev.text)} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-0.5">
                      <p className="text-xs font-bold text-foreground truncate">{alert.title}</p>
                      <span className={cn('text-[10px] font-bold px-2 py-0.5 rounded-full border flex-shrink-0', sts.style)}>
                        {sts.label}
                      </span>
                    </div>
                    <p className="text-[10px] text-muted-foreground line-clamp-1">{alert.desc}</p>
                    <div className="flex items-center gap-3 mt-1.5">
                      <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                        <Search className="w-3 h-3" />
                        {alert.user}
                      </span>
                      <span className="text-[10px] text-muted-foreground/60 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {alert.time}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          })}
        </motion.section>
      </div>

      {/* ── Alert Detail Drawer ── */}
      <Drawer.Root open={selectedAlert !== null} onOpenChange={(open) => { if (!open) closeDrawer() }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10 max-h-[85vh] overflow-y-auto">
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-foreground/20" />
              </div>

              {selectedAlert && (
                <div className="px-6 pb-8 safe-area-bottom space-y-5">
                  {/* Header */}
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold', severityConfig[selectedAlert.severity].bg, severityConfig[selectedAlert.severity].text)}>
                        {severityConfig[selectedAlert.severity].label}
                      </span>
                      <span className="text-[10px] font-mono text-muted-foreground">{selectedAlert.id}</span>
                    </div>
                    <Drawer.Title className="font-display text-xl font-semibold text-foreground">
                      {selectedAlert.title}
                    </Drawer.Title>
                    <p className="text-sm text-muted-foreground mt-1">{selectedAlert.desc}</p>
                  </div>

                  {/* User Details */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">用戶資訊</h4>
                    <div className="p-4 rounded-xl bg-surface-elevated space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-muted-foreground">用戶</span>
                        <span className="text-sm font-bold text-foreground">{selectedAlert.user}</span>
                      </div>
                      {detail && (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">餘額</span>
                            <span className="text-sm font-bold font-mono text-gold">{detail.balance}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">總手數</span>
                            <span className="text-sm font-mono text-foreground">{detail.hands}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">總存款</span>
                            <span className="text-sm font-mono text-emerald-400">{detail.totalDeposit}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">總提款</span>
                            <span className="text-sm font-mono text-foreground">{detail.totalWithdraw}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-muted-foreground">抽水貢獻</span>
                            <span className="text-sm font-mono text-sky-400">{detail.rakeCollected}</span>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Evidence */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">證據摘要</h4>
                    <div className="space-y-1.5">
                      {evidence.map((item, i) => (
                        <div key={i} className="flex items-start gap-2 p-3 rounded-xl bg-surface-elevated">
                          <ShieldAlert className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                          <span className="text-xs text-foreground">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="space-y-2">
                    <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">操作</h4>

                    {/* Action Buttons (hidden when an action mode is active) */}
                    {actionMode === null && (
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          onClick={() => setActionMode('suspend')}
                          className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-red-500/10 border border-red-500/20 active:scale-95 transition-transform"
                        >
                          <UserX className="w-5 h-5 text-red-400" />
                          <span className="text-[10px] font-bold text-red-400">停用帳號</span>
                        </button>
                        <button
                          onClick={handleMarkInvestigating}
                          className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20 active:scale-95 transition-transform"
                        >
                          <Eye className="w-5 h-5 text-amber-400" />
                          <span className="text-[10px] font-bold text-amber-400">標記調查</span>
                        </button>
                        <button
                          onClick={() => setActionMode('dismiss')}
                          className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-foreground/5 border border-foreground/10 active:scale-95 transition-transform"
                        >
                          <XCircle className="w-5 h-5 text-muted-foreground" />
                          <span className="text-[10px] font-bold text-muted-foreground">忽略</span>
                        </button>
                      </div>
                    )}

                    {/* Suspend Account Confirmation */}
                    {actionMode === 'suspend' && (
                      <div className="space-y-3">
                        <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20">
                          <p className="text-xs font-bold text-rose-400">確定要停用此帳號？此操作將立即生效。</p>
                        </div>
                        <textarea
                          value={suspendReason}
                          onChange={(e) => setSuspendReason(e.target.value)}
                          placeholder="請輸入停用原因（必填）"
                          rows={3}
                          className="w-full px-3 py-2 rounded-lg bg-surface-elevated border border-foreground/10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-rose-500/40 resize-none"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => { setActionMode(null); setSuspendReason('') }}
                            className="h-11 rounded-xl glass-card text-sm font-semibold text-foreground"
                          >
                            取消
                          </button>
                          <button
                            onClick={handleSuspendConfirm}
                            disabled={!suspendReason.trim()}
                            className={cn(
                              'h-11 rounded-xl text-sm font-bold text-white transition-opacity',
                              suspendReason.trim() ? 'bg-rose-500' : 'bg-rose-500/30 opacity-50'
                            )}
                          >
                            確認停用
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Dismiss Confirmation */}
                    {actionMode === 'dismiss' && (
                      <div className="space-y-3">
                        <textarea
                          value={dismissReason}
                          onChange={(e) => setDismissReason(e.target.value)}
                          placeholder="請輸入忽略原因（必填）"
                          rows={3}
                          className="w-full px-3 py-2 rounded-lg bg-surface-elevated border border-foreground/10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-foreground/20 resize-none"
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <button
                            onClick={() => { setActionMode(null); setDismissReason('') }}
                            className="h-11 rounded-xl glass-card text-sm font-semibold text-foreground"
                          >
                            取消
                          </button>
                          <button
                            onClick={handleDismissConfirm}
                            disabled={!dismissReason.trim()}
                            className={cn(
                              'h-11 rounded-xl text-sm font-bold text-white transition-opacity',
                              dismissReason.trim() ? 'bg-foreground/10' : 'bg-foreground/5 opacity-50'
                            )}
                          >
                            確認忽略
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
